<script>
import { mapGetters, mapActions } from 'vuex'
import Spinner from '@/components/general/spinner.vue'
import BaseDraggableList from '@/components/_base-draggable-list.vue'
import BookableEducatorCard from './bookable-educator-card.vue'

export default {
  name: 'BookableEducatorList',
  components: { BookableEducatorCard, BaseDraggableList, Spinner },
  props: {
    educators: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    addedEducatorIds: {
      type: Array,
      default: () => [],
    },
    isQueueFull: {
      type: Boolean,
      default: false,
    },
    totalScheduleDays: {
      type: Number,
      default: 1,
    },
    showRemoveOverlay: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchTerm: '',
      isDragRemoveTarget: false,
    }
  },
  computed: {
    ...mapGetters({
      isSendToANZUK: 'bookings/isSendToANZUK',
    }),
    showPagination() {
      return (
        this.educators?.length &&
        !this.hasError &&
        this.totalPages > 1 &&
        !this.isLoading
      )
    },
    visibleEducators() {
      return this.educators.filter((e) => !this.addedEducatorIds.includes(e.id))
    },
    sendToANZUK: {
      get() {
        return this.isSendToANZUK
      },
      set(value) {
        this.updateSendToANZUK(value)
      },
    },
  },
  watch: {
    showRemoveOverlay(isVisible) {
      if (isVisible) return

      this.clearDragRemoveTarget()
    },
  },
  methods: {
    ...mapActions({
      updateSendToANZUK: 'bookings/updateSendToANZUK',
    }),
    onSearchSubmit() {
      this.$emit('search', this.searchTerm)
    },
    onPageChange(page) {
      this.$emit('page-change', page)
    },
    onRetry() {
      this.$emit('retry')
    },
    onAddEducator(educator) {
      this.$emit('add', educator)
    },
    clearSearch() {
      this.searchTerm = ''
      this.$emit('search', '')
    },
    clearDragRemoveTarget() {
      this.isDragRemoveTarget = false
    },
    onExternalDragEnter() {
      this.isDragRemoveTarget = true
    },
    onExternalDragLeave() {
      this.clearDragRemoveTarget()
    },
    onExternalDrop({ item }) {
      if (!this.isDragRemoveTarget || !this.showRemoveOverlay) {
        return
      }

      this.clearDragRemoveTarget()
      this.$emit('drag-remove', item)
    },
  },
}
</script>

<template>
  <v-card
    class="fill-height d-flex flex-column p-lg rounded-lg"
    data-testid="bookable-educator-list"
  >
    <div class="mb-lg flex-shrink-0">
      <h4 class="display-xs mb-1">
        {{ $t('bookingCreateV2.selectEducatorsStep.availableTitle') }}
      </h4>
      <p class="text-xs-regular neutrals-grey-500-text mb-0">
        {{ $t('bookingCreateV2.selectEducatorsStep.availableSubtitle') }}
      </p>
    </div>

    <div class="d-flex align-start mb-lg">
      <v-switch
        id="create-booking-form-v2__send-to-anzuk-checkbox"
        v-model="sendToANZUK"
        aria-labelledby="create-booking-form-v2__send-to-anzuk-label"
        aria-describedby="create-booking-form-v2__send-to-anzuk-description"
        class="mt-0 mr-2"
      >
        <template v-slot:label> </template>
      </v-switch>
      <div class="mt-1">
        <p class="text-md-bold mb-1">{{ $t('bookingCreateV2.sendToANZUK') }}</p>
        <p class="text-md mb-0">{{
          $t('bookingCreateV2.sendToANZUKDescription')
        }}</p>
      </div>
    </div>

    <v-text-field
      v-model="searchTerm"
      dense
      outlined
      clearable
      prepend-inner-icon="mdi-magnify"
      :placeholder="$t('bookingCreateV2.selectEducatorsStep.searchPlaceholder')"
      class="mb-lg flex-grow-0 flex-shrink-0 pr-1"
      hide-details
      :disabled="hasError || isLoading || isSendToANZUK"
      data-testid="educator-search-input"
      @keydown.enter="onSearchSubmit"
      @click:clear="clearSearch"
    />

    <div class="bookable-list p-relative rounded-lg d-flex flex-column">
      <div
        v-if="showRemoveOverlay"
        class="remove-overlay d-flex align-center justify-center text-center"
        data-testid="educator-remove-overlay"
      >
        <v-icon size="70" color="error lighten-1">mdi-delete-outline</v-icon>
      </div>

      <template v-if="hasError">
        <div
          class="d-flex flex-column flex-grow-1 align-center justify-center pa-6 text-center rounded-xl"
          data-testid="educator-error-state"
        >
          <v-icon size="48" color="red lighten-1" class="mb-3"
            >mdi-alert-circle-outline</v-icon
          >
          <p class="text-sm-semibold mb-1">
            {{ $t('bookingCreateV2.selectEducatorsStep.errorTitle') }}
          </p>
          <p class="text-xs-regular neutrals-grey-500-text mb-4">
            {{ $t('bookingCreateV2.selectEducatorsStep.errorBody') }}
          </p>
          <v-btn outlined color="grey" small @click="onRetry">
            {{ $t('bookingCreateV2.selectEducatorsStep.errorCta') }}
          </v-btn>
        </div>
      </template>

      <template v-else-if="isLoading">
        <div
          class="d-flex flex-grow-1 align-center justify-center"
          data-testid="educator-loading-state"
        >
          <Spinner size="48" />
        </div>
      </template>

      <template v-else-if="!educators.length">
        <div
          class="d-flex flex-column flex-grow-1 align-center justify-center text-center rounded-xl mb-8"
          data-testid="educator-empty-state"
        >
          <v-icon size="32" color="grey lighten-1" class="mb-3">
            mdi-account-search-outline
          </v-icon>
          <p class="text-sm-semibold mb-1">
            {{ $t('bookingCreateV2.selectEducatorsStep.noResultsTitle') }}
          </p>
          <p class="text-xs-regular neutrals-grey-500-text mb-0 w-75">
            {{
              $t('bookingCreateV2.selectEducatorsStep.noResultsMessage', [
                $t('app.companyNameShort'),
              ])
            }}
          </p>
        </div>
      </template>

      <template v-else>
        <div class="pr-1 overflow-y-auto bookable-scroll-container">
          <BaseDraggableList
            class="fill-height"
            :class="{
              'pointer-events-none': isSendToANZUK,
              'opacity-50': isSendToANZUK || isQueueFull,
            }"
            :items="visibleEducators"
            group="bookable-educator"
            :accepts-groups="showRemoveOverlay ? ['preferred-educator'] : []"
            item-key="id"
            :drag-enabled="!isQueueFull"
            :clone-on-drag="['preferred-educator']"
            :hide-ghost="['preferred-educator']"
            fill-drop-area
            @external-drag-enter="onExternalDragEnter"
            @external-drag-leave="onExternalDragLeave"
            @external-drop="onExternalDrop"
          >
            <template v-slot="{ item: educator }">
              <BookableEducatorCard
                :educator="educator"
                :is-queue-full="isQueueFull"
                :total-schedule-days="totalScheduleDays"
                @add="onAddEducator"
              />
            </template>
          </BaseDraggableList>
        </div>
      </template>
    </div>

    <div class="d-flex justify-center mt-6 flex-shrink-0">
      <v-pagination
        :value="currentPage"
        class="candidate-list__pagination"
        color="primary"
        :length="totalPages"
        :total-visible="7"
        :disabled="!showPagination"
        data-testid="educator-pagination"
        @input="onPageChange"
      />
    </div>
  </v-card>
</template>

<style lang="scss" scoped>
@import '@/styles/_r2b-colours.scss';

.bookable-scroll-container {
  flex: 1 1 auto;
  min-height: 0;
}

.remove-overlay {
  position: absolute;
  z-index: 4;
  pointer-events: none;
  background-color: rgba(248, 205, 211, 0.2);
  border-radius: 8px;
  outline: 2px dashed $semantics-error-300;
  outline-offset: -2px;
  inset: 0;
}
</style>

<style lang="scss">
@import '@/styles/candidate/candidate-list-pagination.scss';
</style>
